import moment from "moment";
import { useEffect, useState } from "react";
import Datatable from "../../component/datatable";
import Meta from "../../interface/meta";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { setAppLoading } from "../../redux/app.reducer";
import { getBatchTransferLogsApi } from "../../api/wallet";
import { IBatchLog } from "../../interface/wallet";
import Pagination from "../../component/pagination";
import { moneyDisplay } from "../../utils/money";

const WithdrawBatchLogs = () => {
  interface IFilter {
    begin: string;
    end: string;
    status: string;
  }
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<IBatchLog[]>([]);
  const [meta, setMeta] = useState<Meta>({
    page: 0,
    limit: 10,
    totalCount: 0,
    hasMore: true,
  });
  const [filter, setFilter] = useState<IFilter>({
    begin: "",
    end: "",
    status: "",
  });

  const fetchBatchLogs = async (
    filter: IFilter,
    page: number,
    limit: number,
  ) => {
    try {
      setLoading(true);
      const { data } = await getBatchTransferLogsApi({
        status: filter.status || undefined,
        begin:
          filter.begin !== "" ? moment(filter.begin).utc().unix() : undefined,
        end: filter.end !== "" ? moment(filter.end).utc().unix() : undefined,
        page,
        limit,
      });

      setLogs(data.results ?? []);
      setMeta({
        page: page,
        limit: limit,
        totalCount: data.meta.totalCount,
        hasMore: data.meta.hasMore,
      });
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  useEffect(() => {
    fetchBatchLogs(filter, 1, meta.limit);
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return '<span class="badge badge-success">สำเร็จ</span>';
      case "failed":
        return '<span class="badge badge-danger">ล้มเหลว</span>';
      case "partial":
        return '<span class="badge badge-warning">บางส่วน</span>';
      case "pending":
        return '<span class="badge badge-info">กำลังประมวลผล</span>';
      default:
        return '<span class="badge badge-secondary">' + status + "</span>";
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-4-lg my-1 ml-auto mr-3">
          <div className="input-group input-daterange d-flex align-items-center">
            <input
              type="date"
              className="form-control"
              value={filter.begin}
              placeholder="เริ่มต้น"
              onChange={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  begin: e.target.value,
                }));
              }}
            />
            <div className="input-group-addon mx-2">ถึง</div>
            <input
              type="date"
              className="form-control"
              value={filter.end}
              placeholder="สิ้นสุด"
              onChange={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  end: e.target.value,
                }));
              }}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-row mb-2">
            <h3 className="mt-4 ml-1">
              ทั้งหมด {meta.totalCount.toLocaleString("en-US")} รายการ
            </h3>
            <div
              className="d-flex flex-row align-items-end"
              style={{ gap: "1rem" }}
            >
              <div style={{ width: 150 }}>
                <span className="text-muted tx-13">สถานะ</span>
                <select
                  className="form-control"
                  value={filter.status}
                  onChange={(e) => {
                    setFilter((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }));
                  }}
                >
                  <option value="">ทั้งหมด</option>
                  <option value="success">สำเร็จ</option>
                  <option value="failed">ล้มเหลว</option>
                  <option value="partial">บางส่วน</option>
                  <option value="pending">กำลังประมวลผล</option>
                </select>
              </div>
            </div>
          </div>

          <Datatable
            data={logs}
            columns={[
              {
                title: "Batch Code",
                width: 200,
                data: "batchCode",
              },
              {
                title: "เวลาประมวลผล",
                width: 180,
                data: null,
                render(data, type, row: IBatchLog, meta) {
                  return row.processedAt !== ""
                    ? moment(row.processedAt).format("DD/MM/yyyy HH:mm:ss")
                    : "-";
                },
              },
              {
                title: "ผู้ใช้",
                width: 150,
                data: "userName",
                render(data, type, row: IBatchLog, meta) {
                  return data || row.userId || "-";
                },
              },
              {
                title: "จำนวนรายการ",
                width: 100,
                data: null,
                render(data, type, row: IBatchLog, meta) {
                  return String(row.walletIds?.length || 0);
                },
              },
              {
                title: "ยอดรวม",
                width: 150,
                data: "totalAmount",
                render(data, type, row: IBatchLog, meta) {
                  return `<span class="text-success">${moneyDisplay(
                    Math.abs(data),
                  )}</span>`;
                },
              },
              {
                title: "สถานะ",
                data: "status",
                width: 100,
                render(data: string, type, row: IBatchLog, meta) {
                  return getStatusBadge(data);
                },
              },
              {
                title: "BCEL Transaction",
                width: 150,
                data: "bcelTransactionId",
                render(data, type, row: IBatchLog, meta) {
                  return data || "-";
                },
              },
              {
                title: "ผู้ดำเนินการ",
                width: 120,
                data: "triggeredBy",
                render(data, type, row: IBatchLog, meta) {
                  return data === "system" ? "ระบบ (Auto)" : data || "-";
                },
              },
              {
                title: "Error",
                width: 200,
                data: null,
                render(data, type, row: IBatchLog, meta) {
                  if (row.bcelErrorCode || row.bcelErrorMessage) {
                    return `<span class="text-danger" title="${row.bcelErrorMessage || ""}">${row.bcelErrorCode || ""}: ${(row.bcelErrorMessage || "").substring(0, 30)}...</span>`;
                  }
                  return "-";
                },
              },
            ]}
          />

          <Pagination
            active={meta.page}
            limit={meta.limit}
            onPageChange={(page) => {
              fetchBatchLogs(filter, page, meta.limit);
            }}
            pageRangeDisplayed={5}
            total={meta.totalCount}
          />
        </div>
      </div>
    </>
  );
};

export default WithdrawBatchLogs;
